import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Visibility, VisibilityOff, Email as EmailIcon, Lock as LockIcon, Person as PersonIcon, Phone as PhoneIcon } from '@mui/icons-material';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase';
import { createUserProfile, createCoachRegistration } from '../utils/firebaseUtils';
import { useNavigate } from 'react-router-dom';

const CoachRegistrationForm = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [price1Person, setPrice1Person] = useState('');
  const [price2People, setPrice2People] = useState('');
  const [price3People, setPrice3People] = useState('');
  const [price4People, setPrice4People] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (password !== confirmPassword) {
      setError('Slaptažodžiai turi sutapti');
      setLoading(false);
      return;
    }
    if (password.length < 6) {
      setError('Slaptažodis turi būti bent 6 simbolių');
      setLoading(false);
      return;
    }
    if (!email.trim()) {
      setError('El. paštas yra privalomas');
      setLoading(false);
      return;
    }
    if (!fullName.trim()) {
      setError('Vardas ir pavardė yra privalomi');
      setLoading(false);
      return;
    }
    if (!phoneNumber.trim()) {
      setError('Telefono numeris yra privalomas');
      setLoading(false);
      return;
    }

    try {
      const result = await createUserWithEmailAndPassword(auth, email.trim(), password);
      const userId = result.user.uid;

      await createUserProfile(userId, {
        fullName: fullName.trim(),
        phoneNumber: phoneNumber.trim(),
        email: email.trim()
      });

      await createCoachRegistration(userId, {
        email: email.trim(),
        fullName: fullName.trim(),
        phoneNumber: phoneNumber.trim(),
        price1Person: price1Person.trim(),
        price2People: price2People.trim(),
        price3People: price3People.trim(),
        price4People: price4People.trim()
      });

      navigate('/', { state: { registrationSuccess: true } });
    } catch (err) {
      console.error(err);
      switch (err.code) {
        case 'auth/email-already-in-use':
          setError('Vartotojas su tokiu el. paštu jau egzistuoja');
          break;
        case 'auth/weak-password':
          setError('Slaptažodis per silpnas');
          break;
        case 'auth/invalid-email':
          setError('Neteisingas el. pašto formatas');
          break;
        default:
          setError(err.message || 'Registracijos klaida');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 6, px: 2 }}>
      <Container maxWidth="sm">
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 700, textAlign: 'center' }}>
          Trenerio registracija
        </Typography>
        <Paper sx={{ p: 3 }} component="form" onSubmit={handleSubmit}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          <TextField
            fullWidth
            required
            type="email"
            label="El. paštas"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon /></InputAdornment> }}
          />
          <TextField
            fullWidth
            required
            type={showPassword ? 'text' : 'password'}
            label="Slaptažodis"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={{ mb: 2 }}
            inputProps={{ minLength: 6 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><LockIcon /></InputAdornment>,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <TextField
            fullWidth
            required
            type={showConfirmPassword ? 'text' : 'password'}
            label="Pakartokite slaptažodį"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><LockIcon /></InputAdornment>,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <TextField
            fullWidth
            required
            label="Vardas ir pavardė"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon /></InputAdornment> }}
          />
          <TextField
            fullWidth
            required
            label="Telefono numeris"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            placeholder="+370..."
            sx={{ mb: 2 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon /></InputAdornment> }}
          />
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Kainos (€/val)
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
            <TextField
              fullWidth
              label="1 asmuo"
              type="number"
              value={price1Person}
              onChange={(e) => setPrice1Person(e.target.value)}
              InputProps={{
                endAdornment: <InputAdornment position="end">€</InputAdornment>,
                inputProps: { min: 0, step: 0.01 }
              }}
            />
            <TextField
              fullWidth
              label="2 asmenys"
              type="number"
              value={price2People}
              onChange={(e) => setPrice2People(e.target.value)}
              InputProps={{
                endAdornment: <InputAdornment position="end">€</InputAdornment>,
                inputProps: { min: 0, step: 0.01 }
              }}
            />
            <TextField
              fullWidth
              label="3 asmenys"
              type="number"
              value={price3People}
              onChange={(e) => setPrice3People(e.target.value)}
              InputProps={{
                endAdornment: <InputAdornment position="end">€</InputAdornment>,
                inputProps: { min: 0, step: 0.01 }
              }}
            />
            <TextField
              fullWidth
              label="4 asmenys"
              type="number"
              value={price4People}
              onChange={(e) => setPrice4People(e.target.value)}
              InputProps={{
                endAdornment: <InputAdornment position="end">€</InputAdornment>,
                inputProps: { min: 0, step: 0.01 }
              }}
            />
          </Box>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{ py: 1.5, fontWeight: 600 }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Registruotis'}
          </Button>
        </Paper>
      </Container>
    </Box>
  );
};

export default CoachRegistrationForm;
